"use client";

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'next/navigation';
import NewsCard from '@/components/ui/NewsCard';
import { NewsItem } from '@/types/news';
import { ApiResponse } from '@/types/api';
import { News } from '@/types';
import SearchBar from '@/components/ui/SearchBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faHome
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '@/context/LanguageContext';

interface NewsWithRaw extends News {
  raw: NewsItem;
}

import { useUnit } from '@/context/UnitContext';

// ...

export default function NewsIndexContent() {
  const { unitId } = useUnit();
  const { language } = useTranslation();
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
  const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [newsData, setNewsData] = useState<NewsWithRaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed
  const [totalPages, setTotalPages] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const limit = 9; // Items per page

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Reset to first page when search query changes
    setCurrentPage(0);
  }, [searchQuery]);

  useEffect(() => {
    if (!unitId) return;

    const fetchNews = async () => {
      setLoading(true);
      try {
        const keywordParam = searchQuery ? `&keyword=${encodeURIComponent(searchQuery)}` : '';
        const response = await axios.post<ApiResponse<NewsItem[]>>('/api/doServices', {
          method: 'GET',
          endpoint: `web/news?limit=${limit}&page=${currentPage + 1}&type=1&unit_id=${unitId}&order=PUBLISHED_DATE DESC${keywordParam}`
        });

        const apiData = response.data;

        if (apiData && apiData.data) {
          const mappedNews: NewsWithRaw[] = apiData.data.map((item: NewsItem) => ({
            id: item.id.toString(),
            title: item.title_idn || item.title_en,
            slug: item.slug,
            excerpt: item.content_idn ? item.content_idn.replace(/<[^>]+>/g, '').substring(0, 150) + '...' : '',
            content: item.content_idn || item.content_en,
            image: item.filename ? `https://unmul.ac.id/uploads/posts/${item.filename}` : `/api/doServicesProxy?endpoint=web/file/news/${item.id}&isImage=true`,
            category: item.tags?.[0]?.title || 'Berita',
            categoryColor: 'bg-unmulGreen text-white',
            date: new Date(item.published_date).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }),
            raw: item, // Store raw data
          }));
          setNewsData(mappedNews);

          const pages = Number(apiData.metadata?.total_page) || 0;
          setTotalPages(pages);
        } else {
          // Handle case where no data is returned (e.g. search found nothing)
          setNewsData([]);
          setTotalPages(0);
        }
      } catch (error: any) {
        console.error('❌ Error fetching news:', error.response?.data || error.message);
        setNewsData([]); // Reset data on error
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [currentPage, unitId, searchQuery]);

  // Pagination helpers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible);

    if (end - start < maxVisible) {
      start = Math.max(0, end - maxVisible);
    }

    // Previous button
    if (currentPage > 0) {
      pages.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      );
    }

    // Page numbers
    for (let i = start; i < end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${i === currentPage
            ? 'bg-unmulGreen text-white'
            : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
        >
          {i + 1}
        </button>
      );
    }

    // Show ellipsis if there are more pages
    if (end < totalPages) {
      pages.push(
        <span key="ellipsis" className="w-10 h-10 flex items-center justify-center text-gray-400">
          ...
        </span>
      );
      pages.push(
        <button
          key={totalPages - 1}
          onClick={() => handlePageChange(totalPages - 1)}
          className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    if (currentPage < totalPages - 1) {
      pages.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      );
    }

    return pages;
  };

  return (
    <>
      {/* Hero Header */}
      <section className="pt-36 pb-16 bg-unmulGreen relative overflow-hidden">
        {/* SVG Ornaments Background */}
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
          </svg>
          {/* Dots Pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[url('/img/pattern-dots.svg')] opacity-20 transform rotate-45"></div>
          {/* Circular Blurs */}
          <div className="absolute top-10 right-10 w-48 h-48 bg-unmulYellow rounded-full blur-3xl opacity-30"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl opacity-20"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-white/80 mb-6">
            <a href="/" className="hover:text-white transition"><FontAwesomeIcon icon={faHome} /></a>
            <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
            <span className="text-unmulYellow font-medium">Berita</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4" data-aos={isMounted ? "fade-up" : undefined}>
            Berita <span className="text-unmulYellow">&</span> Informasi
          </h1>
          <p className="text-white/80 max-w-2xl text-lg" data-aos={isMounted ? "fade-up" : undefined} data-aos-delay={isMounted ? "100" : undefined}>
            {language === 'en' 
              ? `Explore the latest news, achievements, and important information from ${siteNameEnglish}.`
              : `Jelajahi berita terbaru, prestasi, dan informasi penting dari ${siteName}.`
            }
          </p>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-12 bg-unmulPale">
        <div className="container mx-auto px-6">
          <SearchBar placeholder="Cari berita..." className="mb-10" />

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unmulGreen"></div>
            </div>
          ) : newsData.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">
                {searchQuery ? `Tidak ada berita untuk keyword "${searchQuery}"` : 'Tidak ada berita saat ini.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {newsData.map((news, index) => (
                  <div key={news.id} data-aos="fade-up" data-aos-delay={index * 50}>
                    <NewsCard news={news} raw={news.raw} />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-12">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
